/**
 * Legal plays (rules §4.3, §5.1, §8).
 *
 * The bower quirks are already inside `effectiveSuit` (see ranking.ts), so
 * nothing here mentions bowers — `cardsOfSuit` simply returns the right set.
 * What is left is the Joker's four-part rule in NT/Misère/Open Misère.
 */
import { SUITS } from '../../core/cards.js';
import { anyOf, cardsOfSuit } from '../../core/legality.js';
import { emptyTrick } from '../../core/trick.js';
import { FH_TRICKS_PER_HAND } from './config.js';
import { fhRanking } from './ranking.js';
import { isMisereMode } from './types.js';
/** `hand.tricks` is empty before the opening lead; treat that as trick 0. */
export function fhCurrentTrick(hand) {
    return hand.tricks[hand.tricks.length - 1] ?? emptyTrick(0, hand.contract?.contractor ?? hand.dealer);
}
/**
 * The suit that must be followed to `trick`.
 *
 * Normally this is `trick.ledSuit`, which the core sets from the led card's
 * effective suit. A floating Joker has no effective suit, so when it is led the
 * suit comes from the leader's nomination instead. We read the nomination back
 * out of `extra.leadNominations` as a fallback, so the module stays correct even
 * if the reducer set `ledSuit` from `effectiveSuit` alone (see module.ledSuitOf).
 */
export function effectiveLedSuit(trick, extra) {
    if (trick.ledSuit !== null)
        return trick.ledSuit;
    return extra.leadNominations[trick.index] ?? null;
}
/** Suits the floating Joker may still be nominated to when leading it
 *  (rules §4.3: "a suit that has not previously been led"). */
export function jokerLeadNominations(hand, seat, config) {
    const extra = hand.extra;
    // Trickster's "Joker Low in Nullo" variant makes the Joker the LOWEST card,
    // i.e. a safe discard. A card that can never win has nothing to nominate, so
    // it simply may not be led (it can still be stranded into the last trick,
    // which the caller's fallback allows).
    if (config.jokerLowInMisere && hand.contract && hand.contract.mode !== 'suit')
        return [];
    const trick = fhCurrentTrick(hand);
    const isLastTrick = trick.index >= FH_TRICKS_PER_HAND - 1;
    // Edge #6: once all four suits have been led the Joker may not be led at all
    // — except to the last trick, where any suit may be named.
    let candidates = isLastTrick ? [...SUITS] : SUITS.filter((s) => !extra.suitsLed.includes(s));
    if (config.jokerCannotCallVoidSuit) {
        // Edge #22 (option, off by default): you may not name a suit you have
        // already shown you cannot follow.
        const mine = extra.revealedVoids[seat] ?? [];
        candidates = candidates.filter((s) => !mine.includes(s));
    }
    return candidates;
}
function jokerIsFloating(contract, extra) {
    // In a suit contract the Joker is structurally the top trump and has no
    // special behaviour at all (rules §5.1).
    return contract.mode !== 'suit' && extra.jokerNominated === null;
}
export function fhLegalPlays(hand, seat, config, ctxIn) {
    const holding = hand.secret.holdings[seat] ?? [];
    if (holding.length === 0)
        return [];
    const contract = hand.contract;
    if (!contract)
        return anyOf(holding);
    const extra = hand.extra;
    const ctx = ctxIn ?? fhRanking(contract, extra, config);
    const trick = fhCurrentTrick(hand);
    const floating = jokerIsFloating(contract, extra);
    const holdsJoker = holding.includes('JOKER');
    // ---------- Leading ----------
    if (trick.plays.length === 0) {
        if (!(floating && holdsJoker))
            return anyOf(holding);
        const nominate = jokerLeadNominations(hand, seat, config);
        if (nominate.length === 0) {
            // Edge #6: cannot lead the Joker. (It can still never be stranded: if the
            // Joker were the only card left it would be the last trick, which is
            // always allowed above.)
            const others = holding.filter((c) => c !== 'JOKER');
            return anyOf(others.length > 0 ? others : holding);
        }
        return holding.map((card) => card === 'JOKER' ? { card, nominate } : { card });
    }
    // ---------- Following ----------
    const led = effectiveLedSuit(trick, extra);
    // A nominated Joker IS a card of its suit here; a left bower is NOT a card of
    // its printed suit but IS a card of trump — both fall out of effectiveSuit.
    const follow = cardsOfSuit(holding, led, ctx);
    if (follow.length > 0)
        return anyOf(follow);
    // Void in the suit led.
    if (floating && holdsJoker) {
        if (isMisereMode(contract.mode)) {
            // Edge #4: in Misère/Open Misère the Joker MUST be played once you are
            // void — it cannot be saved for a friendlier trick.
            return anyOf(['JOKER']);
        }
        // Edge #5: in No Trumps playing it is OPTIONAL — discard anything instead
        // and keep the Joker for a later void.
        return anyOf(holding);
    }
    return anyOf(holding);
}
/**
 * What suit does playing `card` set as the suit led?
 *
 * ARCHITECTURE.md §2.9 specifies this hook on `GameModule`; the shipped
 * `core/module.ts` does not carry it, so the module exports it separately and
 * the reducer should call it rather than defaulting to `effectiveSuit(card)` —
 * a floating Joker lead has no effective suit but does bind the others to the
 * nominated one (rules §4.3).
 */
export function fhLedSuitOf(hand, card, nominate, config) {
    const trick = fhCurrentTrick(hand);
    if (trick.plays.length > 0)
        return effectiveLedSuit(trick, hand.extra);
    const ctx = fhRanking(hand.contract, hand.extra, config);
    return ctx.effectiveSuit(card) ?? nominate ?? null;
}
//# sourceMappingURL=legality.js.map